import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import apiClient from '../services/apiClient';

/**
 * Break-Glass Admin Authentication
 * Emergency admin access when M365 identity is unavailable/compromised
 * Requires: pre-configured admin email + SMS OTP verification
 */
export const OobBreakGlassAdmin: React.FC = () => {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInitiate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await apiClient.post('/api/oob/breakglass/initiate', { email });
      setStep('otp');
      setInfo('If this email is registered as a break-glass admin, a verification code has been queued to the pseudo-SMS email channel.');
      // Don't show which admins exist (prevent enumeration)
    } catch (err: any) {
      // Always same message to prevent enumeration
      setError('If this email is registered as a break-glass admin, a verification code has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await apiClient.post('/api/oob/breakglass/resend', { email });
      setInfo('If this email is registered as a break-glass admin, a new verification code has been queued.');
    } catch {
      setError('Unable to resend code right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/api/oob/breakglass/verify', { 
        email, 
        otp 
      });

      // Store admin session in localStorage
      const adminSession = {
        sessionToken: response.data.adminSessionToken,
        sessionType: 'BreakGlassAdmin',
        sessionExpiresAt: new Date(response.data.sessionExpiresAt),
      };
      localStorage.setItem('oob-admin-session', JSON.stringify(adminSession));

      // Redirect to admin panel
      window.location.href = '/admin/oob';
    } catch (err: any) {
      setError('Invalid verification code or email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5" style={{ maxWidth: '500px' }}>
      <Card className="shadow-lg">
        <Card.Header className="bg-danger text-white">
          <Card.Title className="mb-0">Break-Glass Admin Access</Card.Title>
        </Card.Header>
        <Card.Body className="p-4">
          <p className="text-muted small mb-4">
            Emergency access for administrators when M365 identity is unavailable.
            This access is logged and audited.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}
          {info && <Alert variant="info">{info}</Alert>}

          {step === 'email' ? (
            <Form onSubmit={handleInitiate}>
              <Form.Group className="mb-3">
                <Form.Label>Registered Admin Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="admin@enmax.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Button
                variant="danger"
                type="submit"
                className="w-100"
                disabled={!email.trim() || loading}
              >
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Request SMS Code
              </Button>

              <div className="text-center mt-3">
                <small className="text-muted">An SMS will be sent to your registered phone number.</small>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleVerify}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  disabled
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Verification Code (SMS)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  maxLength={6}
                  required
                />
                <small className="text-muted">Enter the 6-digit code from your pseudo-SMS email message.</small>
              </Form.Group>

              <Button
                variant="danger"
                type="submit"
                className="w-100"
                disabled={otp.length !== 6 || loading}
              >
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Verify & Login
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100 mt-2"
                onClick={handleResend}
                disabled={loading}
              >
                Resend Code
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100 mt-2"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setInfo('');
                }}
                disabled={loading}
              >
                Back
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>

      <div className="mt-4 text-center text-muted small">
        <p>
          <strong>About Break-Glass Access:</strong><br />
          This emergency authentication method is available only to pre-configured administrators.
          All access is logged for security auditing. SMS is simulated by email for this demo environment.
        </p>
      </div>
    </Container>
  );
};

export default OobBreakGlassAdmin;
